import { formatToBRL } from "@/utils/formatToMoney";
import { IDemonstrativeTransfer } from "src/@types/store-conciliation";

import {
  TRANSFER_DIRECTION_LABELS,
  TRANSFER_LIST_LABELS,
  TRANSFER_LIST_TEST_IDS,
} from "./types";

type TransferListProps = {
  readonly transfers: readonly IDemonstrativeTransfer[];
};

type TransferField = { readonly label: string; readonly value: string };

const itemKey = (transfer: IDemonstrativeTransfer): string =>
  `${transfer.direction}-${transfer.operationNumber}-${transfer.operationSequence}-${transfer.nsu}`;

const operatorLabel = (transfer: IDemonstrativeTransfer): string =>
  transfer.operatorName
    ? `${transfer.operatorCode} - ${transfer.operatorName}`
    : String(transfer.operatorCode);

const operationLabel = (transfer: IDemonstrativeTransfer): string =>
  transfer.operationName
    ? `${transfer.operationNumber} - ${transfer.operationName}`
    : String(transfer.operationNumber);

const fieldsOf = (transfer: IDemonstrativeTransfer): TransferField[] => [
  { label: TRANSFER_LIST_LABELS.direction, value: TRANSFER_DIRECTION_LABELS[transfer.direction] },
  { label: TRANSFER_LIST_LABELS.time, value: transfer.time },
  { label: TRANSFER_LIST_LABELS.cashRegister, value: String(transfer.cashRegister) },
  { label: TRANSFER_LIST_LABELS.operator, value: operatorLabel(transfer) },
  { label: TRANSFER_LIST_LABELS.operation, value: operationLabel(transfer) },
  { label: TRANSFER_LIST_LABELS.amount, value: formatToBRL(transfer.amount) },
];

export default function TransferList({
  transfers,
}: TransferListProps): JSX.Element {
  if (transfers.length === 0) {
    return (
      <div className="p-2 sm:p-3 border-t">
        <p
          data-testid={TRANSFER_LIST_TEST_IDS.empty}
          className="text-sm text-gray-500"
        >
          {TRANSFER_LIST_LABELS.empty}
        </p>
      </div>
    );
  }

  return (
    <div
      data-testid={TRANSFER_LIST_TEST_IDS.list}
      className="flex flex-col items-start justify-start w-full p-2 sm:p-3 border-t"
    >
      {transfers.map(
        (transfer: IDemonstrativeTransfer): JSX.Element => (
          <div
            key={itemKey(transfer)}
            data-testid={TRANSFER_LIST_TEST_IDS.item}
            data-direction={transfer.direction}
            className="flex flex-col items-start justify-between my-1 gap-1 w-full border p-2 sm:p-3 rounded-md"
          >
            {fieldsOf(transfer).map(
              (field: TransferField): JSX.Element => (
                <div
                  key={field.label}
                  className="flex gap-2 w-full justify-between text-sm"
                >
                  <p className="font-semibold break-words">{field.label}:</p>
                  <span className="font-[300] text-right ml-2 break-words">
                    {field.value}
                  </span>
                </div>
              ),
            )}
          </div>
        ),
      )}
    </div>
  );
}
